import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Upload, 
  Image, 
  FolderOpen, 
  FileImage,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Folder,
  Plus,
  Edit2,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { nasService } from '../services/nasService';
import userService from '../services/userService';
import LoadingSpinner from '../components/LoadingSpinner';
import ProgressBar from '../components/ProgressBar';
import toast from 'react-hot-toast';

const UserUpload = () => {
  const { user } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedBaseFolder, setSelectedBaseFolder] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingSubfolders, setLoadingSubfolders] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showFolderHint, setShowFolderHint] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({
    totalFiles: 0,
    completedFiles: 0,
    failedFiles: 0,
    totalSize: 0,
    uploadedSize: 0,
    speed: 0,
    timeRemaining: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();

  // Blokuj domyślne zachowanie przeglądarki dla drag-and-drop (tylko poza obszarem uploadu)
  useEffect(() => {
    const handleDragOverGlobal = (e) => {
      // Sprawdź czy zdarzenie pochodzi z obszaru uploadu
      const uploadArea = e.target.closest('[data-upload-area]');
      if (!uploadArea) {
        // Blokuj tylko jeśli nie jesteśmy w obszarze uploadu
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
        }
        return false;
      }
      // Jeśli jesteśmy w obszarze uploadu, pozwól na propagację
      return true;
    };

    const handleDropGlobal = (e) => {
      // Sprawdź czy zdarzenie pochodzi z obszaru uploadu
      const uploadArea = e.target.closest('[data-upload-area]');
      if (!uploadArea) {
        // Blokuj tylko jeśli nie jesteśmy w obszarze uploadu
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Jeśli jesteśmy w obszarze uploadu, pozwól na propagację
      return true;
    };

    // Używamy bubble phase (false), aby pozwolić na propagację do obszaru uploadu
    document.addEventListener('dragover', handleDragOverGlobal, false);
    document.addEventListener('drop', handleDropGlobal, false);
    window.addEventListener('dragover', handleDragOverGlobal, false);
    window.addEventListener('drop', handleDropGlobal, false);

    return () => {
      document.removeEventListener('dragover', handleDragOverGlobal, false);
      document.removeEventListener('drop', handleDropGlobal, false);
      window.removeEventListener('dragover', handleDragOverGlobal, false);
      window.removeEventListener('drop', handleDropGlobal, false);
    };
  }, []);

  // Automatycznie uruchom upload gdy pojawią się pliki w kolejce
  useEffect(() => {
    if (uploadQueue.length > 0 && !isUploading) {
      processUploadQueue();
    }
  }, [uploadQueue.length, isUploading]);

  // Pokaż wskazówkę o wyborze folderu po 3 sekundach jeśli nie wybrano
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!selectedBaseFolder) {
        setShowFolderHint(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [selectedBaseFolder]);

  // Ukryj wskazówkę gdy folder zostanie wybrany
  useEffect(() => {
    if (selectedBaseFolder) {
      setShowFolderHint(false);
    }
  }, [selectedBaseFolder]);

  // Oblicz ogólny postęp uploadu
  useEffect(() => {
    if (uploadQueue.length === 0) {
      setOverallProgress(0);
      setUploadStats({
        totalFiles: 0,
        completedFiles: 0,
        failedFiles: 0,
        totalSize: 0,
        uploadedSize: 0,
        speed: 0,
        timeRemaining: 0
      });
      return;
    }

    const totalFiles = uploadQueue.length;
    const completedFiles = uploadQueue.filter(f => f.status === 'completed').length;
    const failedFiles = uploadQueue.filter(f => f.status === 'error').length;
    const uploadingFiles = uploadQueue.filter(f => f.status === 'uploading');
    
    // Oblicz całkowity rozmiar plików
    const totalSize = uploadQueue.reduce((sum, item) => sum + item.file.size, 0);
    
    // Oblicz przesłany rozmiar (symulacja)
    const uploadedSize = uploadQueue.reduce((sum, item) => {
      if (item.status === 'completed') return sum + item.file.size;
      if (item.status === 'uploading' && item.progress) {
        return sum + (item.file.size * item.progress / 100);
      }
      return sum;
    }, 0);

    const progress = totalSize > 0 ? (uploadedSize / totalSize) * 100 : 0;
    
    setOverallProgress(progress);
    setUploadStats({
      totalFiles,
      completedFiles,
      failedFiles,
      totalSize,
      uploadedSize,
      speed: 0, // TODO: Implement real speed calculation
      timeRemaining: 0 // TODO: Implement time remaining calculation
    });
  }, [uploadQueue]);

  // Pobierz dostępne foldery użytkownika
  const { data: userFolders, isLoading: foldersLoading } = useQuery(
    'user-folders',
    () => nasService.getMappedDrives(),
    {
      onSuccess: (data) => {
        if (data.mappedDrives && data.mappedDrives.length > 0) {
          setSelectedFolder(data.mappedDrives[0].folderPath);
        }
      }
    }
  );

  const uploadFileMutation = useMutation(
    ({ deviceId, folderPath, fileName, fileData }) => 
      nasService.uploadFile(deviceId, folderPath, fileName, fileData),
    {
      onSuccess: (data, variables) => {
        setUploadProgress(prev => ({
          ...prev,
          [variables.fileName]: { status: 'success', progress: 100 }
        }));
        toast.success(`Plik ${variables.fileName} został przesłany`);
      },
      onError: (error, variables) => {
        setUploadProgress(prev => ({
          ...prev,
          [variables.fileName]: { status: 'error', progress: 0 }
        }));
        toast.error(`Błąd podczas przesyłania ${variables.fileName}`);
      }
    }
  );

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    console.log('processFiles - otrzymano pliki:', files);
    const imageFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      console.log('processFiles - plik:', file.name, 'typ:', file.type, 'isImage:', isImage);
      return isImage;
    });
    
    console.log('processFiles - przefiltrowane pliki obrazów:', imageFiles);
    
    if (imageFiles.length !== files.length) {
      toast.error('Można przesyłać tylko pliki graficzne (JPG, PNG, GIF)');
    }
    
    if (imageFiles.length > 0) {
      setSelectedFiles(prev => {
        const newFiles = [...prev, ...imageFiles];
        console.log('processFiles - nowa lista plików:', newFiles);
        toast.success(`Dodano ${imageFiles.length} plików`);
        return newFiles;
      });
    } else {
      console.warn('processFiles - brak plików obrazów do dodania');
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedBaseFolder) {
      setIsDragging(true);
      e.dataTransfer.dropEffect = 'copy';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Sprawdź czy opuszczamy główny kontener, nie tylko dziecko
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    // Sprawdź czy kursor jest poza obszarem kontenera
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedBaseFolder) {
      e.dataTransfer.dropEffect = 'copy';
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!selectedBaseFolder) {
      toast.error('Najpierw wybierz folder docelowy');
      return;
    }

    // Pobierz pliki z dataTransfer
    const files = e.dataTransfer.files;
    console.log('handleDrop - files:', files, 'length:', files?.length);
    
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      console.log('handleDrop - fileArray:', fileArray);
      processFiles(fileArray);
    } else {
      console.error('handleDrop - brak plików w dataTransfer');
      toast.error('Nie znaleziono plików do przesłania');
    }
  };

  const handleBaseFolderSelect = (folder) => {
    setSelectedBaseFolder(folder);
    setCurrentPath(folder.folderPath);
    setSelectedFolder(folder.folderPath);
    setSubfolders([]);
    setFiles([]);
    loadSubfolders(folder);
  };

  // Funkcja sortująca foldery od najnowszych do najstarszych
  const sortFoldersByDate = (folders) => {
    return [...folders].sort((a, b) => {
      // Sortuj po nazwie folderu w kolejności malejącej (od najnowszych do najstarszych)
      // Nazwy folderów mają format: YYYY.MM.DD-HH.MM-app-username
      return b.name.localeCompare(a.name);
    });
  };

  const loadSubfolders = async (folder) => {
    setLoadingSubfolders(true);
    try {
      const response = await userService.browseUserFolder(
        folder.deviceId,
        folder.folderPath
      );
      // Sortuj foldery od najnowszych do najstarszych
      const sortedFolders = sortFoldersByDate(response.folders || []);
      setSubfolders(sortedFolders);
      setFiles(response.files || []);
      console.log('loadSubfolders - response:', response);
      console.log('loadSubfolders - files:', response.files);
    } catch (error) {
      console.error('Błąd ładowania podfolderów:', error);
      toast.error('Nie udało się załadować podfolderów');
    } finally {
      setLoadingSubfolders(false);
    }
  };

  const handleSubfolderClick = async (subfolder) => {
    const newPath = `${currentPath}/${subfolder.name}`;
    setCurrentPath(newPath);
    setSelectedFolder(newPath);
    
    setLoadingSubfolders(true);
    try {
      const response = await userService.browseUserFolder(
        selectedBaseFolder.deviceId,
        newPath
      );
      // Sortuj foldery od najnowszych do najstarszych
      const sortedFolders = sortFoldersByDate(response.folders || []);
      setSubfolders(sortedFolders);
      setFiles(response.files || []);
    } catch (error) {
      console.error('Błąd ładowania podfolderów:', error);
      toast.error('Nie udało się załadować podfolderów');
    } finally {
      setLoadingSubfolders(false);
    }
  };

  const handleBackToParent = () => {
    const pathParts = currentPath.split('/');
    if (pathParts.length > 1) {
      pathParts.pop();
      const newPath = pathParts.join('/');
      setCurrentPath(newPath);
      setSelectedFolder(newPath);
      
      // Zawsze wywołaj loadSubfolders, niezależnie od tego czy to folder bazowy czy nie
      loadSubfolders({ ...selectedBaseFolder, folderPath: newPath });
    }
  };

  const generateFolderName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const username = user?.username || 'user';
    
    return `${year}.${month}.${day}-${hours}.${minutes}-app-${username}`;
  };

  const handleCreateFolder = async () => {
    if (!selectedBaseFolder || !currentPath) {
      toast.error('Wybierz folder główny i lokalizację');
      return;
    }

    setCreatingFolder(true);
    try {
      const folderName = generateFolderName();
      await userService.createFolder(
        selectedBaseFolder.deviceId,
        currentPath,
        folderName
      );
      
      toast.success(`Folder "${folderName}" został utworzony pomyślnie`);
      
      // Odśwież listę podfolderów i plików
      await loadSubfolders({ ...selectedBaseFolder, folderPath: currentPath });
    } catch (error) {
      console.error('Błąd tworzenia folderu:', error);
      toast.error('Nie udało się utworzyć folderu');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleStartEditFolder = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
  };

  const handleCancelEditFolder = () => {
    setEditingFolder(null);
    setEditFolderName('');
  };

  const handleSaveEditFolder = async () => {
    if (!editFolderName.trim()) {
      toast.error('Nazwa folderu nie może być pusta');
      return;
    }

    if (editFolderName === editingFolder.name) {
      handleCancelEditFolder();
      return;
    }

    setRenamingFolder(true);
    try {
      // Wywołaj API do zmiany nazwy folderu
      await userService.renameFolder(
        selectedBaseFolder.deviceId,
        currentPath,
        editingFolder.name,
        editFolderName
      );
      
      toast.success(`Folder został przemianowany na "${editFolderName}"`);
      
      // Odśwież listę podfolderów
      await loadSubfolders({ ...selectedBaseFolder, folderPath: currentPath });
      
      handleCancelEditFolder();
    } catch (error) {
      console.error('Błąd zmiany nazwy folderu:', error);
      toast.error(error.response?.data?.message || 'Nie udało się zmienić nazwy folderu');
    } finally {
      setRenamingFolder(false);
    }
  };

  const handleUpload = () => {
    if (!selectedFiles.length || !selectedFolder) {
      toast.error('Wybierz pliki i folder docelowy');
      return;
    }

    const targetFolder = userFolders.mappedDrives.find(folder => 
      selectedFolder.startsWith(folder.folderPath)
    );

    if (!targetFolder) {
      toast.error('Nie znaleziono wybranego folderu');
      return;
    }

    // Dodaj pliki do kolejki
    const filesToUpload = selectedFiles.map(file => ({
      file,
      id: `${Date.now()}-${Math.random()}`,
      status: 'queued'
    }));

    setUploadQueue(prev => [...prev, ...filesToUpload]);
    
    // Wyczyść wybrane pliki
    setSelectedFiles([]);
    
    // Pokaż małe powiadomienie
    toast.success(`${selectedFiles.length} plików dodanych do kolejki uploadu`, {
      duration: 2000,
      icon: '📤'
    });
  };

  const processUploadQueue = async () => {
    if (isUploading) return;
    
    setIsUploading(true);
    
    const processNextFile = async () => {
      setUploadQueue(currentQueue => {
        const queuedFile = currentQueue.find(item => item.status === 'queued');
        if (!queuedFile) {
          setIsUploading(false);
          return currentQueue;
        }
        
        // Oznacz jako uploadowany
        const updatedQueue = currentQueue.map(item => 
          item.id === queuedFile.id 
            ? { ...item, status: 'uploading' }
            : item
        );
        
        // Przetwórz plik asynchronicznie
        processFile(queuedFile);
        
        return updatedQueue;
      });
    };
    
    const processFile = async (queuedFile) => {
      try {
        // Symuluj postęp uploadu
        const simulateProgress = () => {
          let progress = 0;
          const interval = setInterval(() => {
            progress += Math.random() * 15 + 5; // 5-20% na raz
            if (progress >= 100) {
              progress = 100;
              clearInterval(interval);
            }
            
            setUploadQueue(prev => prev.map(item => 
              item.id === queuedFile.id 
                ? { ...item, progress: Math.round(progress) }
                : item
            ));
          }, 200);
        };

        // Rozpocznij symulację postępu
        simulateProgress();

        const fileData = await readFileAsBase64(queuedFile.file);
        
        const targetFolder = userFolders.mappedDrives.find(folder => 
          selectedFolder.startsWith(folder.folderPath)
        );
        
        await uploadFileMutation.mutateAsync({
          deviceId: targetFolder.deviceId,
          folderPath: selectedFolder,
          fileName: queuedFile.file.name,
          fileData: fileData
        });
        
        // Oznacz jako ukończony
        setUploadQueue(prev => prev.map(item => 
          item.id === queuedFile.id 
            ? { ...item, status: 'completed', progress: 100 }
            : item
        ));
        
        // Odśwież listę plików w folderze po udanym uploadzie
        if (selectedBaseFolder) {
          loadSubfolders({ ...selectedBaseFolder, folderPath: currentPath });
        }
        
        // Pokaż małe powiadomienie o ukończeniu
        toast.success(`${queuedFile.file.name} przesłany`, {
          duration: 1500,
          icon: '✅'
        });
        
        // Usuń z kolejki po 2 sekundach
        setTimeout(() => {
          setUploadQueue(prev => prev.filter(item => item.id !== queuedFile.id));
        }, 2000);
        
        // Przetwórz następny plik po krótkiej przerwie
        setTimeout(processNextFile, 1000);
        
      } catch (error) {
        // Oznacz jako błąd
        setUploadQueue(prev => prev.map(item => 
          item.id === queuedFile.id 
            ? { ...item, status: 'error', progress: 0 }
            : item
        ));
        
        toast.error(`Błąd: ${queuedFile.file.name}`, {
          duration: 3000
        });
        
        // Usuń z kolejki po 5 sekundach
        setTimeout(() => {
          setUploadQueue(prev => prev.filter(item => item.id !== queuedFile.id));
        }, 5000);
        
        // Przetwórz następny plik po krótkiej przerwie
        setTimeout(processNextFile, 1000);
      }
    };
    
    processNextFile();
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImage className="h-5 w-5 text-blue-500" />;
      default:
        return <Image className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
        return <LoadingSpinner size="sm" />;
      default:
        return null;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (foldersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!userFolders?.mappedDrives?.length) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Brak dostępu do folderów
        </h3>
        <p className="text-gray-500">
          Skontaktuj się z administratorem, aby otrzymać dostęp do folderów.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Form */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Wybierz pliki i folder docelowy
          </h3>
        </div>
        <div className="card-body space-y-6">
          {/* Folder Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Folder docelowy
              {!selectedBaseFolder && showFolderHint && (
                <span className="ml-2 text-orange-600 text-xs animate-pulse">
                  ⚠️ Wybierz folder aby kontynuować
                </span>
              )}
            </label>
            
            {/* Base Folder Selection */}
            {!selectedBaseFolder && (
              <div className="grid grid-cols-1 gap-2">
                {userFolders.mappedDrives.map((folder) => (
                  <button
                    key={folder.folderPath}
                    onClick={() => handleBaseFolderSelect(folder)}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${
                      showFolderHint 
                        ? 'folder-highlight hover:bg-orange-100' 
                        : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <FolderOpen className={`h-5 w-5 mr-3 ${
                        showFolderHint ? 'text-orange-600' : 'text-blue-600'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{folder.folderName}</p>
                        <p className="text-xs text-gray-500">{folder.deviceName}</p>
                        {showFolderHint && (
                          <p className="text-xs text-orange-600 mt-1 animate-pulse">
                            👆 Kliknij tutaj aby wybrać folder
                          </p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </div>
            )}

            {/* Subfolder Navigation */}
            {selectedBaseFolder && (
              <div className="space-y-3">
                {/* Current Path */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>Ścieżka:</span>
                    <span className="font-medium break-all">{currentPath}</span>
                  </div>
                  
                  {/* Create Folder Button - Responsive */}
                  <div className="flex justify-start sm:justify-end">
                    <button
                      onClick={handleCreateFolder}
                      disabled={creatingFolder}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
                    >
                      {creatingFolder ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      <span>{creatingFolder ? 'Tworzenie...' : 'Utwórz folder'}</span>
                    </button>
                  </div>
                </div>

                {/* Back Button */}
                {currentPath !== selectedBaseFolder.folderPath && (
                  <button
                    onClick={handleBackToParent}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>COFNIJ</span>
                  </button>
                )}

                {/* Subfolders */}
                {loadingSubfolders ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Ładowanie podfolderów...</span>
                  </div>
                ) : subfolders.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2">
                    {subfolders.map((subfolder) => (
                      <div key={subfolder.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        {editingFolder?.name === subfolder.name ? (
                          // Edycja folderu
                          <div className="flex items-center flex-1 space-x-2">
                            <Folder className="h-5 w-5 text-gray-600 mr-3 flex-shrink-0" />
                            <input
                              type="text"
                              value={editFolderName}
                              onChange={(e) => setEditFolderName(e.target.value)}
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEditFolder();
                                if (e.key === 'Escape') handleCancelEditFolder();
                              }}
                            />
                            <button
                              onClick={handleSaveEditFolder}
                              disabled={renamingFolder}
                              className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              {renamingFolder ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={handleCancelEditFolder}
                              className="p-1 text-red-600 hover:text-red-800"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          // Widok normalny
                          <>
                            <button
                              onClick={() => handleSubfolderClick(subfolder)}
                              className="flex items-center flex-1"
                            >
                              <Folder className="h-5 w-5 text-gray-600 mr-3" />
                              <span className="text-sm font-medium text-gray-900">{subfolder.name}</span>
                            </button>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditFolder(subfolder);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edytuj nazwę folderu"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Brak podfolderów w tym folderze
                  </div>
                )}

                {/* Files in folder */}
                {files.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Pliki w folderze ({files.length})
                    </h4>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2">
                      {files.map((file) => {
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
                        const fileSize = file.size ? formatFileSize(file.size) : '';
                        
                        // Utwórz URL do pobierania pliku
                        const getFileUrl = () => {
                          if (!selectedBaseFolder || !file.path) return null;
                          const encodedPath = encodeURIComponent(file.path);
                          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';
                          const token = localStorage.getItem('token');
                          return `${API_BASE_URL}/users/download/${selectedBaseFolder.deviceId}/${encodedPath}?token=${encodeURIComponent(token)}`;
                        };
                        
                        const fileUrl = getFileUrl();
                        
                        const handleImageClick = (e) => {
                          e.preventDefault();
                          if (fileUrl && isImage) {
                            window.open(fileUrl, '_blank');
                          }
                        };
                        
                        return (
                          <div
                            key={file.name}
                            className={`relative group bg-white rounded-lg border border-gray-200 overflow-hidden transition-all ${
                              isImage && fileUrl ? 'hover:shadow-lg cursor-pointer' : 'hover:shadow-md'
                            }`}
                            onClick={isImage && fileUrl ? handleImageClick : undefined}
                            title={isImage && fileUrl ? 'Kliknij aby otworzyć w nowym oknie' : file.name}
                          >
                            {isImage && fileUrl ? (
                              <div className="aspect-square bg-gray-100 overflow-hidden">
                                <img
                                  src={fileUrl}
                                  alt={file.name}
                                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                                  onError={(e) => {
                                    // Jeśli obraz się nie załadował, pokaż ikonę
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                  }}
                                />
                              </div>
                            ) : isImage ? (
                              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                                <FileImage className="h-6 w-6 text-gray-400" />
                              </div>
                            ) : (
                              <div className="aspect-square bg-gray-50 flex items-center justify-center">
                                <Image className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                            <div className="p-1">
                              <p className="text-xs font-medium text-gray-900 truncate" title={file.name}>
                                {file.name}
                              </p>
                              {fileSize && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {fileSize}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {files.length === 0 && subfolders.length === 0 && !loadingSubfolders && (
                  <div className="text-center py-4 text-gray-500">
                    Folder jest pusty
                  </div>
                )}

                {/* Reset Selection */}
                <button
                  onClick={() => {
                    setSelectedBaseFolder(null);
                    setCurrentPath('');
                    setSelectedFolder('');
                    setSubfolders([]);
                    setFiles([]);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Wybierz inny folder główny
                </button>
              </div>
            )}
          </div>

          {/* File Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wybierz zdjęcia
              {!selectedBaseFolder && showFolderHint && (
                <span className="ml-2 text-gray-500 text-xs">
                  (najpierw wybierz folder powyżej)
                </span>
              )}
            </label>
            <div 
              data-upload-area
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragEnter(e);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragOver(e);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragLeave(e);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDrop(e);
              }}
              style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-all duration-300 ${
                !selectedBaseFolder && showFolderHint
                  ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  : isDragging
                  ? 'border-blue-500 bg-blue-50 border-solid'
                  : 'border-gray-300 hover:border-gray-400 cursor-pointer'
              }`}
            >
              <div className="space-y-1 text-center">
                <Upload className={`mx-auto h-12 w-12 ${
                  !selectedBaseFolder && showFolderHint ? 'text-gray-300' : 'text-gray-400'
                }`} />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className={`relative rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 ${
                      !selectedBaseFolder && showFolderHint
                        ? 'cursor-not-allowed text-gray-400'
                        : 'cursor-pointer bg-white text-blue-600 hover:text-blue-500'
                    }`}
                  >
                    <span>Wybierz pliki</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={!selectedBaseFolder}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">lub przeciągnij tutaj</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF do 10MB
                </p>
                {!selectedBaseFolder && showFolderHint && (
                  <p className="text-xs text-orange-600 mt-2 animate-pulse">
                    ⚠️ Najpierw wybierz folder docelowy
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Wybrane pliki ({selectedFiles.length})
              </h4>
              <div className="space-y-3">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <ProgressBar
                      progress={uploadProgress[file.name]?.progress || 0}
                      status={uploadProgress[file.name]?.status || 'pending'}
                      fileName={file.name}
                      fileSize={file.size}
                      speed={0}
                      timeRemaining={0}
                      showDetails={true}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Overall Progress Bar */}
          {uploadQueue.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-blue-900">
                  Postęp przesyłania
                </h4>
                <div className="text-xs text-blue-700">
                  {uploadStats.completedFiles}/{uploadStats.totalFiles} plików
                </div>
              </div>
              
              <ProgressBar
                progress={overallProgress}
                status="uploading"
                fileName={`Przesyłanie ${uploadStats.totalFiles} plików`}
                fileSize={uploadStats.totalSize}
                speed={uploadStats.speed}
                timeRemaining={uploadStats.timeRemaining}
                showDetails={false}
              />
              
              <div className="mt-3 flex justify-between text-xs text-blue-700">
                <span>
                  Ukończone: {uploadStats.completedFiles}
                  {uploadStats.failedFiles > 0 && (
                    <span className="ml-2 text-red-600">
                      Błędy: {uploadStats.failedFiles}
                    </span>
                  )}
                </span>
                <span>
                  {Math.round(overallProgress)}% ukończone
                </span>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0">
            <button
              onClick={handleUpload}
              disabled={!selectedFiles.length || !selectedFolder}
              className={`btn-primary flex items-center justify-center w-full sm:w-auto ${
                !selectedBaseFolder && showFolderHint 
                  ? 'folder-required opacity-50 cursor-not-allowed' 
                  : ''
              }`}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? `Przesyłanie... (${uploadQueue.filter(f => f.status === 'uploading').length})` : 'Prześlij pliki'}
            </button>
            
            {!selectedBaseFolder && showFolderHint && (
              <div className="text-sm text-red-600 animate-pulse text-center sm:text-right">
                ⚠️ Wybierz folder docelowy aby móc przesłać pliki
              </div>
            )}
            
            {/* Mały wskaźnik uploadu */}
            {uploadQueue.length > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span>
                    {uploadQueue.filter(f => f.status === 'uploading').length > 0 && 'Przesyłanie...'}
                    {uploadQueue.filter(f => f.status === 'completed').length > 0 && `✅ ${uploadQueue.filter(f => f.status === 'completed').length} ukończone`}
                    {uploadQueue.filter(f => f.status === 'error').length > 0 && `❌ ${uploadQueue.filter(f => f.status === 'error').length} błędy`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Available Folders Info */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Dostępne foldery
          </h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {userFolders.mappedDrives.map((folder) => (
              <div key={folder.folderPath} className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                <FolderOpen className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{folder.folderName}</p>
                  <p className="text-xs text-gray-500">{folder.deviceName}</p>
                  <p className="text-xs text-gray-400">{folder.folderPath}</p>
                  <div className="flex space-x-2 mt-1">
                    {folder.permissions.read && <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">Odczyt</span>}
                    {folder.permissions.write && <span className="text-xs bg-green-100 text-green-800 px-1 rounded">Zapis</span>}
                    {folder.permissions.upload && <span className="text-xs bg-purple-100 text-purple-800 px-1 rounded">Upload</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserUpload;
